// components
import Input from '@components/Input/Input';
import Tooltip from '@components/Tooltip/Tooltip';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import { notificationContext } from '@context/notifications';
import { faLink, faSortAlt, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getURIFromURLFieldValue } from '@modules/card-helpers';
// utils
import { formatEmailSignatureSocialLinksType } from '@modules/email-signatures-helpers';
import { AnimatePresence, motion } from 'motion/react';
import { type FC, useCallback, useContext, useState } from 'react';
import type { CardUrlType } from 'types';

// consts and types
import { BRAND_COLORS } from '../../consts';
import EmailSignatureIcon from '../../EmailSignatureIcon';
import type { EmailSignatureSocialLink } from '../../types';
import { EmailSignatureLockLayerForInput } from '../EmailSignatureLockLayer';
import styles from './EmailSignatureEditorSocialFieldContainer.module.scss';
// hooks and context
import { EmailSignatureEditorSocialPanelContext } from './EmailSignatureEditorSocialPanel';

type EmailSignatureSocialFieldContainerProps = EmailSignatureSocialLink & {
  isLocked: boolean;
};

const EmailSignatureSocialFieldContainer: FC<
  React.PropsWithChildren<EmailSignatureSocialFieldContainerProps>
> = ({ id, type, value, isLocked }) => {
  const { emailSignatureDispatch, emailSignature } = useContext(emailSignatureSideEditorServicesContext);
  const { isInSwapMode, onSwapActivate, currentFieldIdToSwap } = useContext(
    EmailSignatureEditorSocialPanelContext,
  );
  const { notify } = useContext(notificationContext);
  const [showURLPreview, setShowURLPreview] = useState(!!value);

  const handleInputBlur = useCallback(() => {
    setShowURLPreview(true);
  }, []);

  const handleInputPaste = useCallback(() => {
    setShowURLPreview(true);
  }, []);

  return (
    <motion.div
      layout="position"
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      key={id}
      className={styles['social-field-container']}
    >
      <span className={styles['social-field-brand-icon']}>
        <EmailSignatureIcon
          inlineStyle={{
            color: BRAND_COLORS[type],
          }}
          icon={type}
          size="1x"
        />
      </span>
      <span className={styles['social-field-input-container']}>
        <Input
          disabled={isLocked}
          label={formatEmailSignatureSocialLinksType(type)}
          value={value}
          placeholder={'Username / URL'}
          onChange={(newValue) =>
            emailSignatureDispatch({
              type: 'UPDATE_SOCIAL_LINK_VALUE',
              value: { fieldId: id, updatedFieldValue: newValue },
            })
          }
          hideAllErrorMessages
          enableClearButton={!isLocked}
          onBlur={handleInputBlur}
          onPaste={handleInputPaste}
          size="small"
          autoCorrect="off"
        />

        {isLocked && (
          <EmailSignatureLockLayerForInput
            message={'You cannot edit this link, please contact your admin if you have any questions'}
          />
        )}
      </span>
      <div className={styles['url-preview']} data-has-value={!!value}>
        <AnimatePresence>
          {value && showURLPreview && (
            <Tooltip
              size="small"
              label={`This is the URL we will send people to when they click your ${formatEmailSignatureSocialLinksType(
                type,
              )} field`}
              aria-label="URL Preview message"
            >
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.2 } }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                className={styles['url-preview-content-container']}
                onClick={(event) => {
                  event.stopPropagation();
                  navigator.clipboard.writeText(value).then(() => {
                    notify({
                      id: 'clipboard-success',
                      type: 'info',
                      title: 'Copied to clipboard',
                      size: 'small',
                      timeout: 1500,
                    });
                  });
                }}
                type="button"
              >
                <span className={styles['url-preview-icon']}>
                  <FontAwesomeIcon icon={faLink} />
                </span>
                <span className={styles['url-preview-value']}>
                  {getURIFromURLFieldValue(type as CardUrlType, value) ?? value}
                </span>
              </motion.button>
            </Tooltip>
          )}
        </AnimatePresence>
      </div>
      <span className={styles['social-action-icons']}>
        {!isInSwapMode && (
          <button
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              emailSignatureDispatch({ type: 'DELETE_SOCIAL_LINK', value: id });
            }}
            className={styles['clickable-icons-buttons']}
          >
            <FontAwesomeIcon className={styles['clickable-icons-contents']} icon={faTrash} />
          </button>
        )}
        {emailSignature.socialLinksOrder.length > 1 && (
          <button
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onSwapActivate(id);
            }}
            className={styles['clickable-icons-buttons']}
          >
            {isInSwapMode && id !== currentFieldIdToSwap ? (
              <p>Swap</p>
            ) : (
              <FontAwesomeIcon icon={faSortAlt} className={styles['clickable-icons-contents']} />
            )}
          </button>
        )}
      </span>
    </motion.div>
  );
};

export default EmailSignatureSocialFieldContainer;
